import type { SiteData, Service } from '../shared/types/site.js';
import seoConfig from '../../data/seo.json';

interface SeoPayload {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogImage: string;
  robots: string;
  schemaJson: string | null;
}

const DEFAULT_SITE_URL = seoConfig.defaultSiteUrl;
const DEFAULT_OG_IMAGE = seoConfig.defaultOgImage;
const FALLBACK_SERVICES: Service[] = seoConfig.fallbackServices;
const SEO_COPY = seoConfig.copy;
const ORGANIZATION = seoConfig.organization;
const SCHEMA_ID = 'seo-structured-data';

function normalizeSiteUrl(siteUrl?: string): string {
  return (siteUrl || DEFAULT_SITE_URL).replace(/\/+$/, '');
}

function getSiteUrl(): string {
  if (typeof window === 'undefined' || !window.location?.origin) return DEFAULT_SITE_URL;
  return normalizeSiteUrl(window.location.origin);
}

function normalizePath(path: string): string {
  const trimmed = (path || '/').replace(/\/+$/, '') || '/';
  return trimmed === '' ? '/' : trimmed;
}

function toAbsoluteUrl(path: string, siteUrl?: string): string {
  const base = normalizeSiteUrl(siteUrl);
  const normalizedPath = normalizePath(path);
  return normalizedPath === '/' ? `${base}/` : `${base}${normalizedPath}`;
}

function getServices(siteData: SiteData): Service[] {
  return siteData.services?.length ? siteData.services : FALLBACK_SERVICES;
}

function truncate(text: string, max = 160): string {
  const singleLine = text.replace(/\s+/g, ' ').trim();
  if (singleLine.length <= max) return singleLine;
  const shortened = singleLine.slice(0, max - 1);
  const lastSpace = shortened.lastIndexOf(' ');
  return `${(lastSpace > 90 ? shortened.slice(0, lastSpace) : shortened).trimEnd()}…`;
}

function getHomeDescription(siteData: SiteData): string {
  return truncate(siteData.hero?.tagline || SEO_COPY.homeDescription);
}

function getContactEmail(siteData: SiteData): string {
  return siteData.contact?.email || ORGANIZATION.defaultEmail;
}

function getServiceBySlug(siteData: SiteData, slug: string): Service | undefined {
  return getServices(siteData).find((service) => service.slug === slug);
}

function getOrganizationSchema(siteData: SiteData, siteUrl?: string): string {
  const base = normalizeSiteUrl(siteUrl);
  const email = getContactEmail(siteData);
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: ORGANIZATION.name,
    url: `${base}/`,
    logo: `${base}${DEFAULT_OG_IMAGE}`,
    image: `${base}${DEFAULT_OG_IMAGE}`,
    description: getHomeDescription(siteData),
    email,
    sameAs: ORGANIZATION.sameAs,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: ORGANIZATION.contactType,
        email,
        url: `${base}/contact-us`,
      },
    ],
  });
}

function getBreadcrumbSchema(items: Array<{ name: string; path: string }>, siteUrl?: string): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path, siteUrl),
    })),
  });
}

function getBreadcrumbSchemaForPath(path: string, siteData: SiteData, siteUrl?: string): string | null {
  const normalizedPath = normalizePath(path);

  const staticPages: Record<string, string> = {
    '/services': 'Services',
    '/our-work': 'Our Work',
    '/technologies': 'Technologies',
    '/career': 'Career',
    '/about-us': 'About Us',
    '/software-outsourcing-kosovo': 'Software Outsourcing in Kosova',
    '/contact-us': 'Contact Us',
  };

  if (staticPages[normalizedPath]) {
    return getBreadcrumbSchema(
      [
        { name: 'Home', path: '/' },
        { name: staticPages[normalizedPath], path: normalizedPath },
      ],
      siteUrl
    );
  }

  if (normalizedPath.startsWith('/services/')) {
    const slug = normalizedPath.split('/').filter(Boolean)[1];
    const service = slug ? getServiceBySlug(siteData, slug) : undefined;
    const serviceName = service?.title || SEO_COPY.serviceFallbackTitle;
    return getBreadcrumbSchema(
      [
        { name: 'Home', path: '/' },
        { name: 'Services', path: '/services' },
        { name: serviceName, path: normalizedPath },
      ],
      siteUrl
    );
  }

  return null;
}

function getSeoPayload(path: string, siteData: SiteData, siteUrl?: string): SeoPayload {
  const normalizedPath = normalizePath(path);
  const canonicalUrl = toAbsoluteUrl(normalizedPath, siteUrl);
  const ogImage = `${normalizeSiteUrl(siteUrl)}${DEFAULT_OG_IMAGE}`;
  const knownPublicPath =
    normalizedPath === '/' ||
    normalizedPath === '/services' ||
    normalizedPath === '/our-work' ||
    normalizedPath === '/technologies' ||
    normalizedPath === '/career' ||
    normalizedPath === '/about-us' ||
    normalizedPath === '/software-outsourcing-kosovo' ||
    normalizedPath === '/contact-us' ||
    (normalizedPath.startsWith('/services/') &&
      Boolean(normalizedPath.split('/').filter(Boolean)[1] && getServiceBySlug(siteData, normalizedPath.split('/').filter(Boolean)[1]!)));

  if (!knownPublicPath) {
    return {
      title: SEO_COPY.notFoundTitle,
      description: SEO_COPY.notFoundDescription,
      canonicalUrl,
      ogTitle: SEO_COPY.notFoundTitle,
      ogDescription: SEO_COPY.notFoundDescription,
      ogUrl: canonicalUrl,
      ogImage,
      robots: 'noindex,follow',
      schemaJson: null,
    };
  }

  if (normalizedPath === '/') {
    const description = getHomeDescription(siteData);
    return {
      title: SEO_COPY.homeTitle,
      description,
      canonicalUrl,
      ogTitle: SEO_COPY.homeTitle,
      ogDescription: description,
      ogUrl: canonicalUrl,
      ogImage,
      robots: 'index,follow',
      schemaJson: getOrganizationSchema(siteData, siteUrl),
    };
  }

  if (normalizedPath === '/services') {
    const description = truncate(SEO_COPY.servicesDescription);
    return {
      title: SEO_COPY.servicesTitle,
      description,
      canonicalUrl,
      ogTitle: SEO_COPY.servicesTitle,
      ogDescription: description,
      ogUrl: canonicalUrl,
      ogImage,
      robots: 'index,follow',
      schemaJson: getBreadcrumbSchemaForPath(normalizedPath, siteData, siteUrl),
    };
  }

  if (normalizedPath.startsWith('/services/')) {
    const slug = normalizedPath.split('/').filter(Boolean)[1];
    const service = slug ? getServiceBySlug(siteData, slug) : undefined;
    const serviceTitle = service?.title || SEO_COPY.serviceFallbackTitle;
    const description = truncate(service?.description || SEO_COPY.serviceFallbackDescription);
    return {
      title: `${serviceTitle} | ARKRON`,
      description,
      canonicalUrl,
      ogTitle: `${serviceTitle} | ARKRON`,
      ogDescription: description,
      ogUrl: canonicalUrl,
      ogImage,
      robots: 'index,follow',
      schemaJson: getBreadcrumbSchemaForPath(normalizedPath, siteData, siteUrl),
    };
  }

  const entries: Record<string, [string, string]> = {
    '/our-work': [SEO_COPY.ourWorkTitle, siteData.ourWork?.intro || SEO_COPY.ourWorkDescription],
    '/technologies': [SEO_COPY.technologiesTitle, siteData.technologies?.intro || SEO_COPY.technologiesDescription],
    '/career': [SEO_COPY.careerTitle, SEO_COPY.careerDescription],
    '/about-us': [SEO_COPY.aboutTitle, siteData.company?.body || SEO_COPY.aboutDescription],
    '/software-outsourcing-kosovo': [SEO_COPY.outsourcingKosovoTitle, SEO_COPY.outsourcingKosovoDescription],
    '/contact-us': [SEO_COPY.contactTitle, siteData.contact?.subtitle || SEO_COPY.contactDescription],
  };

  const [title, rawDescription] = entries[normalizedPath] || [SEO_COPY.homeTitle, getHomeDescription(siteData)];
  const description = truncate(rawDescription);
  return {
    title,
    description,
    canonicalUrl,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonicalUrl,
    ogImage,
    robots: 'index,follow',
    schemaJson: getBreadcrumbSchemaForPath(normalizedPath, siteData, siteUrl),
  };
}

function upsertMeta(selector: string, attributes: Record<string, string>, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(selector: string, attributes: Record<string, string>, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function syncStructuredData(schemaJson: string | null): void {
  const existing = document.getElementById(SCHEMA_ID);
  if (!schemaJson) {
    existing?.remove();
    return;
  }

  let script = existing;
  if (!(script instanceof HTMLScriptElement)) {
    script = document.createElement('script');
    script.id = SCHEMA_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = schemaJson;
}

export function syncSeo(path: string, siteData: SiteData): void {
  const seo = getSeoPayload(path, siteData, getSiteUrl());
  document.title = seo.title;
  upsertMeta('meta[name="description"]', { name: 'description' }, seo.description);
  upsertMeta('meta[name="robots"]', { name: 'robots' }, seo.robots);
  upsertLink('link[rel="canonical"]', { rel: 'canonical' }, seo.canonicalUrl);
  upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'ARKRON');
  upsertMeta('meta[property="og:title"]', { property: 'og:title' }, seo.ogTitle);
  upsertMeta('meta[property="og:description"]', { property: 'og:description' }, seo.ogDescription);
  upsertMeta('meta[property="og:url"]', { property: 'og:url' }, seo.ogUrl);
  upsertMeta('meta[property="og:image"]', { property: 'og:image' }, seo.ogImage);
  upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, 'ARKRON logo');
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, seo.ogTitle);
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, seo.ogDescription);
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, seo.ogImage);
  syncStructuredData(seo.schemaJson);
}
